import { type CSSProperties, memo, useCallback, useState } from 'react'

interface ProgressiveImageProps {
  // Full-size image, lazy loaded and faded in over the low-res one once it has arrived
  src: string
  // Low-res copy shown first; without it this is a plain lazy <img>
  lowResSrc?: string | null
  alt: string
  width: number
  height: number
  className?: string
}

/**
 * Shows the low-res copy right away and fades the full image in over it once it has loaded.
 * The copy stays mounted under the full image and CSS hides it after the fade, so the
 * thumbnail never blinks empty. If the copy is missing (an image uploaded before copies
 * existed) it is dropped silently; if the full image fails, the copy stays.
 */
export const ProgressiveImage = memo(
  ({ src, lowResSrc, alt, width, height, className }: ProgressiveImageProps) => {
    // Keyed by URL, so a new image starts over without an effect resetting state
    const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
    const [failedLowResSrc, setFailedLowResSrc] = useState<string | null>(null)
    const handleLoad = useCallback(() => setLoadedSrc(src), [src])
    const handleLowResError = useCallback(() => setFailedLowResSrc(lowResSrc ?? null), [lowResSrc])

    if (!lowResSrc) {
      return (
        <img
          className={className}
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
        />
      )
    }

    const loaded = loadedSrc === src
    const style: CSSProperties = { width, height }
    return (
      <span
        className={`progressive-image ${className ?? ''}`.trim()}
        style={style}
        data-loaded={loaded}>
        {failedLowResSrc === lowResSrc ? null : (
          <img
            className="progressive-image__low-res"
            src={lowResSrc}
            alt=""
            aria-hidden="true"
            width={width}
            height={height}
            decoding="async"
            onError={handleLowResError}
          />
        )}
        <img
          className="progressive-image__full"
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
        />
      </span>
    )
  },
)

ProgressiveImage.displayName = 'ProgressiveImage'
